/**
 * What to say when a generation fails, and whether trying again can help.
 *
 * A failure used to reach the vendor as the model provider's own sentence, in a
 * red box, with nothing to do about it — so a picture that would have come back
 * on a second attempt simply stayed broken, and one that never could kept
 * inviting them to spend credits finding out.
 *
 * The distinction is the whole point: a provider hiccup is worth another go, a
 * refused prompt is not, and saying which is which is more useful than either
 * the raw error or a generic apology.
 */

export interface FailureCopy {
    /** One line, in the vendor's terms. */
    text: string;
    /** Whether running it again could plausibly produce a different result. */
    retryable: boolean;
}

/** Refusals and quota walls: the same request will fail the same way. */
const PERMANENT: Array<{ pattern: RegExp; key: string; text: string }> = [
    {
        pattern: /terms of service|content polic|safety|violat|refus|not allowed|prohibited/i,
        key: 'BLOCKED',
        text: 'The model would not make this one — the request looks like something it is not '
            + 'allowed to generate. Changing the wording or the product usually clears it.',
    },
    {
        pattern: /\b(quota|limit reached|daily limit|insufficient|out of credits|billing)\b/i,
        key: 'QUOTA',
        text: 'You have hit a generation limit, so this one could not run. It will work again '
            + 'once the limit resets.',
    },
];

/** Everything a second attempt has a real chance of getting past. */
const TRANSIENT = /timeout|timed out|econn|socket|network|503|502|500|temporarily|overload|rate.?limit|try again/i;

export function describeFailure(error?: string): FailureCopy {
    const raw = (error || '').trim();

    for (const rule of PERMANENT) {
        if (rule.pattern.test(raw)) return { text: rule.text, retryable: false };
    }
    if (TRANSIENT.test(raw)) {
        return {
            text: 'The generator was busy or unreachable, so this one did not finish. Running it '
                + 'again usually works.',
            retryable: true,
        };
    }
    // Unknown, and unknown is worth one more attempt — most of these have been
    // transient. The provider's own words go on the row so it is diagnosable.
    return {
        text: raw
            ? `This one did not finish: ${trimReason(raw)}`
            : 'This one did not finish, and the generator did not say why.',
        retryable: true,
    };
}

/** Provider errors run to paragraphs; the first sentence carries the meaning. */
function trimReason(raw: string): string {
    const firstSentence = raw.split(/(?<=[.!?])\s/)[0] || raw;
    return firstSentence.length > 160 ? `${firstSentence.slice(0, 157)}…` : firstSentence;
}
